import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { API } from '../App';
import { useTranslation } from '../LanguageContext';
import Flag from './Flag';
import {
  Menu, X, Car, User, LogOut, Plus, LayoutDashboard, Search,
  BookOpen, ChevronDown, Gauge, Shield, Globe, Settings, ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, language, changeLanguage, languages } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const backendBase = API.replace('/api', '');
  // Build a usable avatar URL whether avatar is a remote URL (Google) or a relative path (uploaded)
  const avatarSrc = (() => {
    const raw = user?.avatar_url || user?.avatar || user?.picture || '';
    if (!raw) return '';
    if (raw.startsWith('http')) return raw;
    return `${backendBase}${raw}`;
  })();
  const displayName = user?.name || (user?.email ? user.email.split('@')[0] : 'You');
  const initials = displayName.trim().split(/\s+/).map(s => s[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const [searchResults, setSearchResults] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const searchRef = useRef(null);
  const langRef = useRef(null);
  const searchTimeout = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Live search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const [carsRes, wikiRes] = await Promise.all([
          axios.get(`${API}/cars`, { params: { brand: searchQuery, limit: 3 } }),
          axios.get(`${API}/wiki/search`, { params: { q: searchQuery } })
        ]);
        setSearchResults({
          cars: carsRes.data.slice(0, 3),
          brands: wikiRes.data.brands?.slice(0, 3) || [],
          models: wikiRes.data.models?.slice(0, 3) || [],
        });
      } catch (e) {
        console.error(e);
      }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery]);

  // Close search on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchFocused(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cars?search=${encodeURIComponent(searchQuery.trim())}`);
      clearSearch();
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cars?search=${encodeURIComponent(searchQuery.trim())}`);
      clearSearch();
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { href: '/cars', label: t('nav.buy'), icon: Car, desc: 'Browse marketplace' },
    { href: '/sell', label: t('nav.sell'), icon: Plus, desc: 'List your vehicle' },
    { href: '/magazine', label: t('nav.magazine'), icon: BookOpen, desc: 'CarHipo Magazine' },
    { href: '/wiki', label: t('nav.wiki'), icon: BookOpen, desc: 'Car encyclopedia' },
    { href: '/vin-check', label: t('nav.vin'), icon: Shield, desc: 'Verify history' },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  const showDropdown = searchFocused && searchResults && searchQuery.length >= 2;
  const hasResults = showDropdown && (searchResults.cars.length > 0 || searchResults.brands.length > 0 || searchResults.models.length > 0);

  return (
    <>
      <nav className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-12 gap-3">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
              <img src="/logo-nav.webp" alt="CarHipo" className="h-8 w-auto" />
            </Link>

            {/* Search Bar (Desktop) */}
            <div className="hidden lg:flex flex-1 max-w-sm relative" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative w-full">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <input
                  type="text"
                  placeholder={t('nav.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  className="w-full h-8 pl-8 pr-8 text-xs bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500/50 focus:bg-white/8 transition-all outline-none"
                  data-testid="nav-search"
                />
                {searchQuery && (
                  <button type="button" onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    <X className="w-3 h-3 text-gray-500 hover:text-white" />
                  </button>
                )}
              </form>

              {/* Search Results Dropdown */}
              {showDropdown && (
                <div className="absolute top-full mt-1.5 w-full bg-[#0a0a0f] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in">
                  {hasResults ? (
                    <>
                      {searchResults.cars.length > 0 && (
                        <div>
                          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-white/3">Cars</div>
                          {searchResults.cars.map(car => (
                            <Link key={car.id} to={`/cars/${car.id}`} onClick={clearSearch}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-cyan-500/10 transition-colors">
                              <img src={car.images?.[0] || ''} alt="" className="w-12 h-8 rounded object-cover bg-white/5" />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate text-white">{car.title}</div>
                                <div className="text-[10px] text-cyan-500 font-semibold">&euro;{car.price?.toLocaleString()}</div>
                              </div>
                              <Gauge className="w-3 h-3 text-gray-600" />
                            </Link>
                          ))}
                        </div>
                      )}
                      {searchResults.brands.length > 0 && (
                        <div>
                          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-white/3">Brands</div>
                          {searchResults.brands.map(brand => (
                            <Link key={brand.id} to={`/wiki/${brand.name}`} onClick={clearSearch}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-cyan-500/10 transition-colors">
                              <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center">
                                <BookOpen className="w-3.5 h-3.5 text-cyan-500/70" />
                              </div>
                              <div>
                                <div className="text-xs font-medium text-white">{brand.name}</div>
                                <div className="text-[10px] text-gray-500">{brand.country} &middot; Since {brand.founded}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                      {searchResults.models.length > 0 && (
                        <div>
                          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-white/3">Models</div>
                          {searchResults.models.map(model => (
                            <Link key={model.id} to={`/wiki/model/${model.id}`} onClick={clearSearch}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-cyan-500/10 transition-colors">
                              <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center">
                                <Car className="w-3.5 h-3.5 text-gray-500" />
                              </div>
                              <div>
                                <div className="text-xs font-medium text-white">{model.brand} {model.name}</div>
                                <div className="text-[10px] text-gray-500">{model.body_types?.join(', ')}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-4 py-6 text-center text-xs text-gray-500">No results for &quot;{searchQuery}&quot;</div>
                  )}
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-0.5 ml-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-cyan-500 transition-colors rounded-md hover:bg-white/5"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <span className="flex items-center gap-1.5">
                    <link.icon className="w-3.5 h-3.5" />
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>

            {/* Language Switcher + Auth Section (Desktop) */}
            <div className="hidden md:flex items-center gap-2 ml-2">
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  data-testid="lang-switcher-btn"
                >
                  <Flag code={currentLang.code} size={16} />
                  <ChevronDown className="w-3 h-3" />
                </button>
                {langOpen && (
                  <div className="absolute top-full mt-1 right-0 w-44 bg-[#0a0a0f] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { changeLanguage(lang.code); setLangOpen(false); }}
                        className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs hover:bg-cyan-500/10 transition-colors ${
                          language === lang.code ? 'text-cyan-500 bg-cyan-500/5' : 'text-gray-300'
                        }`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        <Flag code={lang.code} size={18} />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5 h-8 px-2"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-7 h-7 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30 overflow-hidden" data-testid="user-avatar">
                        {avatarSrc ? (
                          <img src={avatarSrc} alt={displayName} data-testid="user-avatar-img" className="w-full h-full object-cover" referrerPolicy="no-referrer"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.querySelector('.avatar-fallback').style.display = 'flex'; }} />
                        ) : null}
                        <span className={`avatar-fallback w-full h-full ${avatarSrc ? 'hidden' : 'flex'} items-center justify-center text-[10px] font-bold text-cyan-500`}>{initials}</span>
                      </div>
                      <span className="text-xs font-medium max-w-[80px] truncate">{displayName}</span>
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 bg-[#0a0a0a] border-white/10">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer text-xs" data-testid="dashboard-link">
                        <LayoutDashboard className="w-3.5 h-3.5" />
                        {t('nav.dashboard')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/sell" className="flex items-center gap-2 cursor-pointer text-xs" data-testid="sell-car-link">
                        <Plus className="w-3.5 h-3.5" />
                        {t('nav.sellCar')}
                      </Link>
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-xs text-cyan-500" data-testid="admin-link">
                          <Settings className="w-3.5 h-3.5" />
                          {t('nav.admin')}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400 text-xs"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login" data-testid="login-link">
                    <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-white/5 h-8 text-xs">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="register-link">
                    <Button size="sm" className="bg-cyan-500 text-black hover:bg-cyan-400 font-semibold h-8 text-xs shadow-neon hover:shadow-neon-lg transition-all">
                      {t('nav.signup')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-1.5 text-gray-400 hover:text-white ml-auto"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-Page Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] md:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'visible' : 'invisible'
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute inset-0 bg-[#060a12] flex flex-col transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 h-14 border-b border-white/5 shrink-0">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center">
              <img src="/logo-nav.webp" alt="CarHipo" className="h-7 w-auto" />
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:text-white active:bg-white/10 transition-colors"
              data-testid="mobile-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {/* Search */}
            <div className="px-5 pt-5 pb-3">
              <form onSubmit={handleMobileSearchSubmit} className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search cars, brands, models..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 text-sm bg-white/5 border border-white/8 rounded-xl text-white placeholder:text-gray-600 outline-none focus:border-cyan-500/40"
                  data-testid="mobile-search-input"
                />
              </form>
            </div>

            {/* Navigation Links */}
            <div className="px-3 py-2">
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-600">Navigate</p>
              {navLinks.map((link, i) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-4 px-3 py-3.5 rounded-xl text-gray-200 active:bg-white/5 transition-colors"
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                    <link.icon className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{link.label}</div>
                    <div className="text-xs text-gray-500">{link.desc}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
                </Link>
              ))}
            </div>

            {/* User Section */}
            <div className="px-3 py-2 border-t border-white/5 mt-1">
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-600">Account</p>
              {user ? (
                <>
                  {/* User Profile Card */}
                  <div className="mx-3 mb-3 p-3.5 rounded-xl bg-gradient-to-r from-cyan-500/10 to-transparent border border-cyan-500/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                        <User className="w-5 h-5 text-cyan-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate">{user.name}</div>
                        <div className="text-xs text-gray-500 truncate">{user.email}</div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-4 px-3 py-3.5 rounded-xl text-gray-200 active:bg-white/5 transition-colors"
                    data-testid="mobile-dashboard-link"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                      <LayoutDashboard className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{t('nav.dashboard')}</div>
                      <div className="text-xs text-gray-500">Your listings & activity</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </Link>

                  {user.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-4 px-3 py-3.5 rounded-xl text-cyan-400 active:bg-cyan-500/5 transition-colors"
                      data-testid="mobile-admin-link"
                    >
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-cyan-500" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{t('nav.admin')}</div>
                        <div className="text-xs text-cyan-500/60">Admin panel</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-cyan-500/40" />
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-4 w-full px-3 py-3.5 rounded-xl text-red-400 active:bg-red-500/5 transition-colors"
                    data-testid="mobile-logout-btn"
                  >
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-semibold">{t('nav.logout')}</div>
                      <div className="text-xs text-red-400/50">Sign out of account</div>
                    </div>
                  </button>
                </>
              ) : (
                <div className="px-3 flex gap-3">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1" data-testid="mobile-login-link">
                    <button className="w-full h-12 rounded-xl border border-white/10 bg-white/5 text-sm font-semibold text-gray-200 active:bg-white/10 transition-colors">
                      {t('nav.login')}
                    </button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="flex-1" data-testid="mobile-register-link">
                    <button className="w-full h-12 rounded-xl bg-cyan-500 text-sm font-bold text-black active:bg-cyan-400 transition-colors shadow-neon">
                      {t('nav.signup')}
                    </button>
                  </Link>
                </div>
              )}
            </div>

            {/* Language Section */}
            <div className="px-3 py-2 border-t border-white/5 mt-1">
              <button
                onClick={() => setMobileLangOpen(!mobileLangOpen)}
                className="flex items-center gap-4 w-full px-3 py-3.5 rounded-xl text-gray-200 active:bg-white/5 transition-colors"
                data-testid="mobile-lang-toggle"
              >
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">{t('common.language') || 'Language'}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1.5"><Flag code={currentLang.code} size={14} /> {currentLang.name}</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${mobileLangOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileLangOpen && (
                <div className="px-3 pb-2 grid grid-cols-2 gap-2">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => { changeLanguage(lang.code); setMobileLangOpen(false); }}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
                        language === lang.code
                          ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20'
                          : 'bg-white/5 text-gray-400 border border-transparent active:bg-white/10'
                      }`}
                      data-testid={`mobile-lang-${lang.code}`}
                    >
                      <Flag code={lang.code} size={18} />
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 px-5 py-4 border-t border-white/5 bg-[#060a12]">
            <p className="text-[10px] text-gray-600 text-center">
              &copy; {new Date().getFullYear()} CarHippo. AI-Powered Car Marketplace.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
